import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {tap, take} from 'rxjs/operators';
import {PreviewDataSourceInfo} from '../../../../../data-source/models/preview-data-source-info.model';
import {PreviewColumns} from '../../../../../data-source/models/preview-columns.model';
import {DataSourceRowNumber} from '../../../../../data-source/models/data-source-row-number.type';
import {ColumnsType} from '../../../../../data-source/models/columns-type.model';
import {IDataSourcePreview} from '../../interfaces/i-data-source-preview';
import {DataSourceFacade} from '../../../../../data-source/facade/data-source.facade';
import {ReplaceDatasourceConfirmDialogComponent} 
  from '../../../../../data-source/components/dialogs/replace-datasource-confirm-dialog/replace-datasource-confirm-dialog.component';
import {ConfirmationReplacementComponent}
  from '../../../../../data-source/components/dialogs/confirmation-replacement/confirmation-replacement.component';
import {MatMenuTrigger} from "@angular/material/menu";

@Component({
  selector: 'morva-preview-table',
  templateUrl: './preview-table.component.html',
  styleUrls: ['./preview-table.component.scss'],
})
export class PreviewTableComponent implements OnInit,OnDestroy {
  @ViewChild('menuTrigger') menu: MatMenuTrigger;
  @ViewChild('searchType') search: ElementRef;
  workspaceId: string;
  type: 'CreateDataSource' | 'ReplacementDataSource' | 'ReplacementExcelWithSql';
  loading: boolean;
  oldDataSourceName: string;
  previewDataSourceInfo: PreviewDataSourceInfo;
  columns: PreviewColumns;
  rowNumber: DataSourceRowNumber;
  data: any;
  isUploading = false;
  columnsTypes: ColumnsType;
  changedColumnsTypes: ColumnsType;
  showColumnsTypes: ColumnsType;
  observables = [];
  hundred: DataSourceRowNumber = 'Hundred';
  twoHundred: DataSourceRowNumber = 'TwoHundred';
  thousand: DataSourceRowNumber = 'Thousand';
  subscriptions: Subscription[] = [];
  columnsDataLength: number[] = [];
  isEmptySheet = false;
  replacementResult: boolean = false
  types = [
    {
      id: 0,
      type: 'Text',
    },
    {
      id: 1,
      type: 'WholeNumber',
    },
    {
      id: 2,
      type: 'DecimalNumber',
    },
    {
      id: 3,
      type: 'Currency',
    },
    {
      id: 4,
      type: 'Date',
    },
    {
      id: 12,
      type: 'JalaliDate'
    },
    {
      id: 5,
      type: 'Boolean',
    },
    {
      id: 6,
      type: 'Binary',
    },
    {
      id: 7,
      type: 'Variant',
    },
  ];
  hasError: boolean;

  constructor(
    private dialogRef: NbDialogRef<any>,
    private dialogService: NbDialogService,
    @Inject('DataSourcePreview') private dataSourcePreviewFacade: IDataSourcePreview,
  ) { }

  ngOnInit(): void {
    this.setSubscribers();
  }

  loadTableContent(rowNumber: DataSourceRowNumber): void {
    this.rowNumber = rowNumber;
    if(this.type === 'CreateDataSource') {
      switch (this.previewDataSourceInfo.dbConnectionType) {
        case 'File':
          this.dataSourcePreviewFacade.
          loadFileOrURLPreviewTableContent(this.workspaceId, this.previewDataSourceInfo, rowNumber);
          break;
        case 'URL':
          this.dataSourcePreviewFacade.
          loadFileOrURLPreviewTableContent(this.workspaceId, this.previewDataSourceInfo, rowNumber);
          break;
        case 'MicrosoftSQLServer':
          this.dataSourcePreviewFacade.
          loadSqlServerPreviewTableContent(this.workspaceId, this.previewDataSourceInfo, rowNumber);
          break;
        case 'WebService':
          this.dataSourcePreviewFacade.loadWebServicePreviewTableContent(this.workspaceId, this.previewDataSourceInfo, rowNumber);
          break;
        case 'Oracle':
          this.dataSourcePreviewFacade.
          loadOraclePreviewTableContent(this.workspaceId, this.previewDataSourceInfo,rowNumber);
          break;
      }
    } else if (this.type === 'ReplacementDataSource') {
      this.dataSourcePreviewFacade.checkUpdateDataSourceData(this.workspaceId,this.previewDataSourceInfo,
          rowNumber, undefined);
    } else if (this.type === 'ReplacementExcelWithSql') {
      this.dataSourcePreviewFacade.
        loadReplacementDataBaseConnectionTables(this.workspaceId, this.previewDataSourceInfo, rowNumber, undefined);
    }
  }

  setSubscribers(): void {
    this.observables.push(this.dataSourcePreviewFacade.getWorkspaceId$().
    pipe(tap(workspaceId => this.workspaceId = workspaceId)));
    this.observables.push(this.dataSourcePreviewFacade.getPreviewDataSourceInfo$().
    pipe(tap(previewDataSourceInfo => this.previewDataSourceInfo = previewDataSourceInfo)));

    this.subscriptions.push(combineLatest(this.observables).subscribe(() => {
      this.loading = true;
      this.loadTableContent(this.hundred);
    }));

    this.subscriptions.push(this.dataSourcePreviewFacade.getPreviewTableContent$().subscribe(
      (columns: PreviewColumns) => {
        this.loading = false;
        this.columns = columns;
        if(columns !== null) {
          if (Object.keys(columns.data).length === 0) {
            this.columnsDataLength = [];
            this.columnsTypes = [];
            this.isEmptySheet = true;
          } else {
            this.setRowLength(columns.data);
            this.setColumnsType(columns.data);
            this.isEmptySheet = false;
          }
        }
      },
    ));

    this.subscriptions.push(this.dataSourcePreviewFacade.getSubmitState$().subscribe(
      (submit) => {
        if(submit === 'submitted') {
          this.isUploading = false;
          this.dataSourcePreviewFacade.setSubmitState('notSubmitted');
          if(this.type === 'ReplacementExcelWithSql') {
            this.dialogRef.close(this.replacementResult);
            return
          }
          this.dialogRef.close();
        } else if (submit === 'notSubmitted') {
          this.isUploading = false;
        }
      },
    ));

    this.subscriptions.push(this.dataSourcePreviewFacade.getErrorTableContent$().subscribe(
      (hasError) => {
        this.hasError = hasError;
      },
    ));
  }

  deletePreviewConnection(): void {
    if(this.type === 'CreateDataSource') {
      this.dataSourcePreviewFacade.deletePreviewConnection(this.previewDataSourceInfo);
    } else if (this.type === 'ReplacementExcelWithSql') {
      this.dataSourcePreviewFacade.deleteReplacementSQLConnectionAndRevokeDataSource(this.previewDataSourceInfo);
    } else {
      this.dataSourcePreviewFacade.deletePreviewReplacementConnection(this.previewDataSourceInfo);
    }
  }

  setColumnType(newValue, key): void {
    this.changedColumnsTypes[key] = newValue;
  }

  openMenuDataType(): void {
    this.search.nativeElement.value = '';
    this.showColumnsTypes = [];
    for(const key in this.columnsTypes) {
      if(key) {
        this.showColumnsTypes[key] = this.columnsTypes[key];
      }
    }
  }

  searchInColumnsTypes(searchKey: string): void {
    this.showColumnsTypes = [];
    for(const key in this.columnsTypes) {
      if(key.toLowerCase().includes(searchKey.toLowerCase())) {
        this.showColumnsTypes[key] = this.columnsTypes[key];
      }
    }
  }

  saveChangedColumnsType(): void {
    this.columnsTypes = [];
    for(const key in this.changedColumnsTypes) {
      if(key) {
        this.columnsTypes[key] = this.changedColumnsTypes[key];
      }
    }
  }

  save(): void {
    if(this.type === 'CreateDataSource') {
      this.isUploading = true;
      this.dataSourcePreviewFacade.createDataSourceTable(this.previewDataSourceInfo,this.workspaceId,this.columnsTypes);
    } else if(this.type === 'ReplacementExcelWithSql') {
      this.convertDataSourceFromExcelToSqlServer()
    } else if(this.type === 'ReplacementDataSource') {
      this.dialogService.open(ConfirmationReplacementComponent, {
        closeOnBackdropClick: false,
        context: {
          oldDataSourceName: this.oldDataSourceName,
          previewDataSourceInfo: this.previewDataSourceInfo,
          workspaceId: this.workspaceId,
          columnsTypes: this.columnsTypes,
          dataSourceWarnings: this.columns.dataSourceWarnings,
        },
      }).onClose.subscribe((isReplaced) => {
        if(isReplaced) {
          this.dialogRef.close();
        } else {
          this.deletePreviewConnection();
        }
      });
    }
  }

  convertDataSourceFromExcelToSqlServer(): void {
    this.getConvertConfirmation()
      .pipe(take(1))
      .subscribe(
        (res: string) => {
          if(res === 'decline') return
          this.replacementResult = true
          this.dataSourcePreviewFacade
            .replaceExcelWithSqlServerDataSource(this.workspaceId, this.previewDataSourceInfo.dataSourceId, this.oldDataSourceName)
        }
      )
    
  }

  getConvertConfirmation(): Observable<any> {
    const dialogRef = 
      this.dialogService.open(ReplaceDatasourceConfirmDialogComponent,
        { context: {
          message: `آﯾﺎ با جایگزینی منبع داده قبلی  با منبع داده جدید “${this.oldDataSourceName}” موافق هستید؟ `,
          title: `تایید جایگزینی داده`,
          type: 'updateDatasourceData'
        }})
    return dialogRef.onClose
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private setRowLength(data: any): void {
    this.columnsDataLength = [];
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        for(let index = 0 ; index < data[key].columnData.length; index++ ) {
          this.columnsDataLength.push(index);
        }
        break;
      }
    }
  }

  private setColumnsType(data: any): void {
    this.columnsTypes = [];
    this.changedColumnsTypes = [];
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        this.columnsTypes[key] = data[key].columnType;
        this.changedColumnsTypes[key] = data[key].columnType;
      }
    }
  }
}
